import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { useClientsQuery } from './hooks/useClientsQuery';
import ClientsList from './components/Clients.List';
import ClientsFilterPanel from './components/Clients.FilterPanel';
import ClientsFilterChips from './components/Clients.FilterChips';
import {
  buildFilterModel,
  countActiveFilters,
  readStoredFilters,
  writeStoredFilters,
} from './utils/clientFilters';

const Clients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(readStoredFilters);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  useEffect(() => {
    writeStoredFilters(filters);
  }, [filters]);

  const queryOptions = useMemo(
    () => ({ filterModel: buildFilterModel(filters), search }),
    [filters, search]
  );

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useClientsQuery(queryOptions);

  const allClients = data?.pages.flatMap((page) => page.results) ?? [];

  const handleSearch = useCallback((value) => {
    setSearch(value);
  }, []);

  const handleChat = (client) => {
    if (client.active_lead_uuid) navigate(`/leads/${client.active_lead_uuid}`);
  };

  const handleDetails = (client) => {
    navigate(`/clients/${client.uuid}`, { state: { from: location.pathname } });
  };

  return (
    <MainLayout
      title={t('nav.clients')}
      showSearch={!filterPanelOpen}
      onSearch={handleSearch}
      searchPlaceholder={t('clients.searchClient')}
      showFilter={true}
      onFilterClick={() => setFilterPanelOpen(true)}
      activeFilterCount={activeFilterCount}
    >
      <Box sx={{ pb: 2 }}>
        <Box sx={{ px: 2 }}>
          <ClientsFilterChips filters={filters} onChange={setFilters} />
          <ClientsList
            clients={allClients}
            isLoading={isLoading}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onFetchNextPage={fetchNextPage}
            onChat={handleChat}
            onDetails={handleDetails}
          />
        </Box>
      </Box>

      <ClientsFilterPanel
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        filters={filters}
        onApply={setFilters}
      />
    </MainLayout>
  );
};

export default Clients;
